'use client';

type SelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

type RequiredProps =
  | {
      multiple?: undefined | false;
      value?: string | null;
      onChange?: (value: string | null) => void;
      options: SelectOption[];
    }
  | {
      multiple: true;
      value?: string[];
      onChange?: (value: string[]) => void;
      options: SelectOption[];
    };

type OptionalProps = {
  label?: string;
  disabled?: boolean;
  invalid?: boolean;
  error?: string;
  isLoading?: boolean;
  name?: string;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  filterable?: boolean;
};

type SelectProps = RequiredProps & OptionalProps;

const Select: React.FC<RequiredProps & OptionalProps> = ({
  disabled,
  invalid,
  isLoading,
  name,
  onBlur,
  onChange,
  options,
  placeholder,
  value,
  filterable,
  multiple,
  label,
  error,
}) => {
  return (
    <sdx-select
      label={label}
      multiple={multiple}
      valid={!invalid}
      validation-message={error}
      ref={(r) => {
        if (!r) return;

        r.oninput = (ev) => {
          if (disabled) return;
          // @ts-expect-error Missing typing
          const values: string[] | undefined = ev.target?.['value'] as string[];
          if (!values) return;
          if (values.length === 0 && !multiple) {
            onChange?.(null);
            return;
          }

          if (multiple) {
            onChange?.(
              options
                .filter((opts) => values.some((v) => v === opts.value))
                .map((opt) => opt.value),
            );
            return;
          }

          const opt = options.find((o) => o.value === values[0]);
          if (!opt) return;
          onChange?.(opt.value);
        };
      }}
      disabled={disabled}
      aria-invalid={invalid}
      loading={isLoading}
      name={name}
      onBlur={() => onBlur?.()}
      // If no placeholder is specified SDX messes up
      // if no value is selected
      placeholder={placeholder}
      keyboard-behavior={filterable ? 'filter' : undefined}
      filter-min-length={filterable ? 2 : undefined}
      max-height={300}
    >
      <sdx-select-list>
        {options.map((o) => (
          <sdx-select-option
            key={o.value}
            disabled={o.disabled}
            value={o.value}
            selected={
              Array.isArray(value)
                ? value.some((v) => v === o.value)
                : value === o.value
                  ? true
                  : undefined
            }
          >
            {o.label}
          </sdx-select-option>
        ))}
      </sdx-select-list>
    </sdx-select>
  );
};

export default Select;
export type { SelectOption, SelectProps };
